import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';
import '../styles/admin-theme.css';

const formatDate = (d) => {
  if (d == null || d === '') return '';
  try {
    if (typeof d === 'string') {
      const s = d.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      if (d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      const date = new Date(d);
      if (Number.isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    if (typeof d.getTime === 'function') {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    return '';
  } catch {
    return '';
  }
};

const AddLicenseModal = ({ show, onHide, companyId, subscriptions, onSuccess, initialLicense }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [planLicenses, setPlanLicenses] = useState([]);
  const [pricebookEntries, setPricebookEntries] = useState([]);
  const [form, setForm] = useState({
    subscription_id: '',
    license_type_id: '',
    quantity: 1,
    start_date: '',
    status: 'DRAFT',
  });
  const isEdit = !!initialLicense;

  const activeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

  useEffect(() => {
    if (show) {
      adminSchemaApi
        .fetchLicenseTypes()
        .then((list) => setLicenseTypes(Array.isArray(list) ? list : []))
        .catch(() => setLicenseTypes([]));
      setPlanLicenses([]);
    }
  }, [show]);

  const selectedSubscription = useMemo(
    () => activeSubscriptions.find((s) => s.id === form.subscription_id),
    [activeSubscriptions, form.subscription_id]
  );

  useEffect(() => {
    if (show && selectedSubscription?.plan_id) {
      adminSchemaApi
        .fetchPlanLicenses(selectedSubscription.plan_id)
        .then((list) => setPlanLicenses(Array.isArray(list) ? list : []))
        .catch(() => setPlanLicenses([]));
    } else {
      setPlanLicenses([]);
    }
  }, [show, selectedSubscription?.plan_id]);

  useEffect(() => {
    if (show && selectedSubscription?.pricebook_id) {
      adminSchemaApi
        .fetchPricebookEntries(selectedSubscription.pricebook_id)
        .then((list) => setPricebookEntries(Array.isArray(list) ? list : []))
        .catch(() => setPricebookEntries([]));
    } else {
      setPricebookEntries([]);
    }
  }, [show, selectedSubscription?.pricebook_id]);

  const allowedLicenseTypeIds = useMemo(() => {
    const planIds = new Set((planLicenses || []).map((pl) => pl.license_type_id));
    const withPrice = new Set(
      (pricebookEntries || [])
        .filter((e) => e.plan_id === selectedSubscription?.plan_id)
        .map((e) => e.productid)
    );
    return new Set([...planIds].filter((id) => withPrice.has(id)));
  }, [planLicenses, pricebookEntries, selectedSubscription?.plan_id]);
  const allowedLicenseTypes = useMemo(
    () => licenseTypes.filter((lt) => allowedLicenseTypeIds.has(lt.id)),
    [licenseTypes, allowedLicenseTypeIds]
  );

  useEffect(() => {
    if (show && activeSubscriptions.length > 0 && !form.subscription_id) {
      const first = activeSubscriptions[0];
      setForm((prev) => ({
        ...prev,
        subscription_id: first.id,
        start_date: prev.start_date || formatDate(first.start_date),
      }));
    }
  }, [show, activeSubscriptions, form.subscription_id]);

  useEffect(() => {
    if (show && initialLicense) {
      setForm({
        subscription_id: initialLicense.subscription_id || '',
        license_type_id: initialLicense.license_type_id || '',
        quantity: initialLicense.quantity || 1,
        start_date: formatDate(initialLicense.start_date),
        status: (initialLicense.status || 'DRAFT').toUpperCase(),
      });
      setError('');
    }
  }, [show, initialLicense]);

  const handleClose = () => {
    setForm({ subscription_id: '', license_type_id: '', quantity: 1, start_date: '', status: 'DRAFT' });
    setError('');
    onHide();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'subscription_id') {
        const sub = activeSubscriptions.find((s) => s.id === value);
        if (sub) next.start_date = formatDate(sub.start_date);
        next.license_type_id = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId || !form.subscription_id) {
      setError('Please select a subscription.');
      return;
    }
    if (!form.license_type_id) {
      setError('Please select a license type.');
      return;
    }
    const qty = Math.max(0, Number(form.quantity));
    if (qty === 0) {
      setError('Quantity must be at least 1.');
      return;
    }
    const sub = activeSubscriptions.find((s) => s.id === form.subscription_id);
    const startDate = form.start_date || (sub && formatDate(sub.start_date));
    if (!startDate) {
      setError('Activation date is required.');
      return;
    }
    if (sub) {
      const subStart = new Date(sub.start_date);
      const subEnd = new Date(sub.end_date);
      const act = new Date(startDate);
      if (act < subStart || act > subEnd) {
        setError('Activation date must be within the subscription period.');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await adminSchemaApi.updateLicense(companyId, form.subscription_id, initialLicense.id, {
          license_type_id: form.license_type_id,
          quantity: qty,
          start_date: startDate,
          status: (form.status || 'ACTIVE').toUpperCase(),
        });
      } else {
        await adminSchemaApi.addLicenseToSubscription(companyId, form.subscription_id, {
          license_type_id: form.license_type_id,
          quantity: qty,
          start_date: startDate,
          status: (form.status || 'ACTIVE').toUpperCase(),
        });
      }
      handleClose();
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err?.message || (isEdit ? 'Failed to update license.' : 'Failed to add license.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">
          {isEdit ? 'Edit License' : 'Add License'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {activeSubscriptions.length === 0 ? (
          <Alert variant="info">Create a subscription first before adding licenses.</Alert>
        ) : (
          <>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Subscription</Form.Label>
                <Form.Select
                  name="subscription_id"
                  value={form.subscription_id}
                  onChange={handleChange}
                  required
                  disabled={isEdit}
                >
                  <option value="">Select subscription</option>
                  {activeSubscriptions.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.billing_cycle} · {formatDate(sub.start_date)} – {formatDate(sub.end_date)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>License type</Form.Label>
                <Form.Select
                  name="license_type_id"
                  value={form.license_type_id}
                  disabled={!form.subscription_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    {form.subscription_id ? 'Select license type' : 'Select subscription first'}
                  </option>
                  {allowedLicenseTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name || lt.code}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Activation date</Form.Label>
                <Form.Control
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleChange}
                  min={form.subscription_id ? formatDate(activeSubscriptions.find((s) => s.id === form.subscription_id)?.start_date) : undefined}
                  max={form.subscription_id ? formatDate(activeSubscriptions.find((s) => s.id === form.subscription_id)?.end_date) : undefined}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={form.status} onChange={handleChange}>
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE" disabled={(selectedSubscription?.status || '').toUpperCase() !== 'ACTIVE'}>
                    Active (subscription must be active)
                  </option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="EXPIRED">Expired</option>
                </Form.Select>
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" className="admin-primary-btn" disabled={saving}>
                  {saving ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save' : 'Add license')}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AddLicenseModal;
